type AnyAttrs = Record<string, string | number | boolean | undefined | null>;

/**
 * Minimal DOM builder so we can render the banner without a framework while
 * still keeping the source readable.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: AnyAttrs = {},
  children: (Node | string | null | undefined)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (value === true) {
      node.setAttribute(key, "");
    } else {
      node.setAttribute(key, String(value));
    }
  }
  for (const child of children) {
    if (child === null || child === undefined) continue;
    node.append(typeof child === "string" ? document.createTextNode(child) : child);
  }
  return node;
}

/**
 * Wait for document.body to exist. The CMP script is loaded first in <head>
 * (intentionally, to set consent defaults before any other script runs), so
 * `<body>` may not be parsed yet when we want to render the banner.
 */
export function waitForBody(): Promise<void> {
  if (document.body) return Promise.resolve();
  return new Promise<void>((resolve) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => resolve(), { once: true });
    } else {
      // readyState is "interactive" but body still null (rare) — poll briefly.
      const id = setInterval(() => {
        if (document.body) {
          clearInterval(id);
          resolve();
        }
      }, 10);
    }
  });
}

export function injectStylesOnce(css: string): void {
  if (document.getElementById("cmp-style")) return;
  const style = document.createElement("style");
  style.id = "cmp-style";
  style.textContent = css;
  (document.head ?? document.documentElement).appendChild(style);
}

export function ensureRoot(): HTMLElement {
  let root = document.querySelector<HTMLElement>("[data-cmp-root]");
  if (!root) {
    root = document.createElement("div");
    root.setAttribute("data-cmp-root", "");
    document.body.appendChild(root);
  }
  return root;
}
